'use client';

import React, { useEffect, useState, useRef } from 'react';
import { StatShowcaseComponent } from '../types/content-components.types.js';
import { ComponentIcon } from './ComponentIcon.js';
import { TrendingUp, TrendingDown } from 'lucide-react';

interface StatShowcaseProps {
  component: StatShowcaseComponent;
}

export const StatShowcase: React.FC<StatShowcaseProps> = ({ component }) => {
  const { props } = component;
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  const getStatColor = (variant?: string) => {
    switch (variant) {
      case 'primary':
        // Muted Sage
        return { bg: 'bg-learn-sage-50', border: 'border-learn-sage-200', text: 'text-learn-sage-600', value: 'text-learn-sage-800' };
      case 'success':
        // Growth Green (muted)
        return { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-600', value: 'text-green-800' };
      case 'warning':
        // Muted Honey
        return { bg: 'bg-learn-honey-50', border: 'border-learn-honey-200', text: 'text-learn-honey-600', value: 'text-learn-honey-800' };
      case 'danger':
        return { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-600', value: 'text-red-800' };
      case 'info':
        // Muted Sky Blue
        return { bg: 'bg-learn-sky-50', border: 'border-learn-sky-200', text: 'text-learn-sky-600', value: 'text-learn-sky-800' };
      case 'highlight':
        // Muted Coral/Clay
        return { bg: 'bg-learn-coral-50', border: 'border-learn-coral-200', text: 'text-learn-coral-600', value: 'text-learn-clay-800' };
      default:
        // Soft neutral
        return { bg: 'bg-learn-sage-50', border: 'border-learn-sage-100', text: 'text-gray-600', value: 'text-gray-800' };
    }
  };

  const gridCols = props.layout === 'row' 
    ? 'grid-cols-1' 
    : props.stats.length === 2 
    ? 'grid-cols-2' 
    : props.stats.length === 3 
    ? 'grid-cols-2 md:grid-cols-3' 
    : 'grid-cols-2 md:grid-cols-2';

  return (
    <div ref={ref} className="w-full">
      {props.title && (
        <h3 className="text-xl font-bold text-gray-900 mb-4">{props.title}</h3>
      )}

      <div className={`grid ${gridCols} gap-4`}>
        {props.stats.map((stat, index) => {
          const colors = getStatColor(stat.variant);
          const animationDelay = `${index * 100}ms`;

          return (
            <div
              key={`stat-${index}`}
              className={`${colors.bg} border-2 ${colors.border} rounded-xl p-5 transition-all duration-200 hover:shadow-md ${
                isVisible ? 'animate-fadeIn' : ''
              }`}
              style={{ 
                animationDelay,
                opacity: isVisible ? undefined : 0
              }}
            >
              {/* Icon */}
              {stat.icon && (
                <div className="mb-3">
                  <ComponentIcon icon={stat.icon} className={`w-6 h-6 ${colors.text}`} />
                </div>
              )}

              {/* Value */}
              <div className="flex items-baseline gap-2 mb-2">
                <div className={`text-3xl md:text-4xl font-bold ${colors.value}`}>
                  {stat.value}
                </div>
                {/* Trend Indicator */}
                {stat.trend && (
                  <div className={`flex items-center ${
                    stat.trend === 'up' ? 'text-green-600' : 
                    stat.trend === 'down' ? 'text-red-600' : 
                    'text-gray-600'
                  }`}>
                    {stat.trend === 'up' && <TrendingUp className="w-5 h-5" />}
                    {stat.trend === 'down' && <TrendingDown className="w-5 h-5" />}
                  </div>
                )}
              </div>

              {/* Label */}
              <div className={`text-sm font-semibold ${colors.text} mb-1`}>
                {stat.label}
              </div>

              {/* Description */}
              {stat.description && (
                <p className="text-xs text-gray-600 leading-relaxed">
                  {stat.description}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

